import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Loader2,
  Save,
  Users,
  Search,
  UserPlus,
  UserMinus,
  Building2,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES, SECTORS, layout, surfaces, fontSizes } from "../styles/theme";
import { Button } from "./ui";
import {
  previewAudienceFilters,
  createAudienceList,
  updateAudienceList,
  fetchTags,
  fetchContacts,
  fetchAppUsers,
} from "../services/api";

const ENGAGEMENT_TIERS = [
  { id: "hot", label: "Hot", color: "#ef4444" },
  { id: "warm", label: "Warm", color: "#f59e0b" },
  { id: "cool", label: "Cool", color: "#3b82f6" },
  { id: "cold", label: "Cold", color: "#94a3b8" },
];

const EMPTY_FILTERS = {
  contactTypes: [],
  engagementTiers: [],
  companies: [],
  tags: [],
  isDecisionMaker: null,
  emailFrequency: null,
  bounceStatus: null,
  countries: [],
  sectors: [],
  searchText: "",
  includeEmails: [],
  excludeEmails: [],
};

const labelStyle = {
  display: "block",
  fontSize: 10,
  color: colors.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 5,
  fontFamily: fonts.body,
  fontWeight: 600,
};

const inputStyle = {
  width: "100%",
  padding: "7px 10px",
  fontSize: 12,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

export default function AudienceListBuilder({ list, onClose, onSaved }) {
  const isEdit = !!list;
  const [name, setName] = useState(list?.name || "");
  const [description, setDescription] = useState(list?.description || "");
  const [filters, setFilters] = useState(
    list?.filters
      ? { ...EMPTY_FILTERS, ...list.filters }
      : { ...EMPTY_FILTERS },
  );
  const [excludeSuppressed, setExcludeSuppressed] = useState(
    list?.excludeSuppressed !== false,
  );
  const [sharedWith, setSharedWith] = useState(list?.sharedWith || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [groupByCompany, setGroupByCompany] = useState(false);
  const previewTimer = useRef(null);

  // Tag/company/country input state
  const [tagInput, setTagInput] = useState("");
  const [companyInput, setCompanyInput] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [crmTags, setCrmTags] = useState([]);
  const [ghlContactTags, setGhlContactTags] = useState([]);
  const [ghlContentTags, setGhlContentTags] = useState([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const tagDropdownRef = useRef(null);

  // Contact include/exclude search state
  const [includeSearch, setIncludeSearch] = useState("");
  const [excludeSearch, setExcludeSearch] = useState("");
  const [includeResults, setIncludeResults] = useState([]);
  const [excludeResults, setExcludeResults] = useState([]);
  const [includeDropdownOpen, setIncludeDropdownOpen] = useState(false);
  const [excludeDropdownOpen, setExcludeDropdownOpen] = useState(false);
  const includeRef = useRef(null);
  const excludeRef = useRef(null);
  const includeTimer = useRef(null);
  const excludeTimer = useRef(null);

  // App users for the "Share With" dropdown
  const [appUsers, setAppUsers] = useState([]);

  // Fetch all tags on mount — exclude hidden GHL tags (category "other")
  useEffect(() => {
    fetchTags()
      .then((res) => {
        const crm = res.crmTags || [];
        const contact = res.ghlContactTags || [];
        const content = res.ghlContentTags || [];
        setCrmTags(crm);
        setGhlContactTags(contact);
        setGhlContentTags(content);
        setAllTags([...crm, ...contact, ...content]);
      })
      .catch(() => {});
    fetchAppUsers()
      .then((res) => setAppUsers(res.users || []))
      .catch(() => {});
  }, []);

  // Close tag dropdown on outside click
  useEffect(() => {
    if (!tagDropdownOpen) return;
    const handler = (e) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(e.target)
      ) {
        setTagDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [tagDropdownOpen]);

  // Close include/exclude dropdowns on outside click
  useEffect(() => {
    if (!includeDropdownOpen && !excludeDropdownOpen) return;
    const handler = (e) => {
      if (
        includeDropdownOpen &&
        includeRef.current &&
        !includeRef.current.contains(e.target)
      )
        setIncludeDropdownOpen(false);
      if (
        excludeDropdownOpen &&
        excludeRef.current &&
        !excludeRef.current.contains(e.target)
      )
        setExcludeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [includeDropdownOpen, excludeDropdownOpen]);

  // Debounced contact search for include
  useEffect(() => {
    clearTimeout(includeTimer.current);
    if (!includeSearch.trim()) {
      setIncludeResults([]);
      return;
    }
    includeTimer.current = setTimeout(() => {
      fetchContacts({ search: includeSearch.trim(), limit: 10 })
        .then((d) => setIncludeResults(d.contacts || []))
        .catch(() => setIncludeResults([]));
    }, 300);
    return () => clearTimeout(includeTimer.current);
  }, [includeSearch]);

  // Debounced contact search for exclude
  useEffect(() => {
    clearTimeout(excludeTimer.current);
    if (!excludeSearch.trim()) {
      setExcludeResults([]);
      return;
    }
    excludeTimer.current = setTimeout(() => {
      fetchContacts({ search: excludeSearch.trim(), limit: 10 })
        .then((d) => setExcludeResults(d.contacts || []))
        .catch(() => setExcludeResults([]));
    }, 300);
    return () => clearTimeout(excludeTimer.current);
  }, [excludeSearch]);

  const updateFilter = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
  }, []);

  // Debounced preview
  useEffect(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      setPreviewing(true);
      previewAudienceFilters(filters, excludeSuppressed)
        .then((data) => setPreview(data))
        .catch(() => setPreview(null))
        .finally(() => setPreviewing(false));
    }, 500);
    return () => clearTimeout(previewTimer.current);
  }, [filters, excludeSuppressed]);

  const toggleArrayFilter = (key, value) => {
    updateFilter(
      key,
      filters[key].includes(value)
        ? filters[key].filter((v) => v !== value)
        : [...filters[key], value],
    );
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !filters.tags.includes(tag)) {
      updateFilter("tags", [...filters.tags, tag]);
    }
    setTagInput("");
  };

  const addCompany = () => {
    const company = companyInput.trim();
    if (company && !filters.companies.includes(company)) {
      updateFilter("companies", [...filters.companies, company]);
    }
    setCompanyInput("");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const { list: updated } = await updateAudienceList(list.id, {
          name,
          description,
          filters,
          excludeSuppressed,
          sharedWith,
        });
        onSaved(updated);
      } else {
        const { list: created } = await createAudienceList({
          name,
          description,
          filters,
          excludeSuppressed,
          sharedWith,
        });
        onSaved(created);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const chipStyle = (active, accentColor) => ({
    padding: "4px 10px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: fonts.body,
    border: `1px solid ${active ? accentColor || colors.accent : colors.border}`,
    background: active ? `${accentColor || colors.accent}18` : "transparent",
    color: active ? accentColor || colors.accent : colors.textSecondary,
    cursor: "pointer",
    transition: "all 0.15s",
  });

  return (
    <>
      <div onClick={onClose} style={surfaces.overlay} />
      <div
        style={{
          ...surfaces.modal,
          top: "3%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 720,
          maxHeight: "94vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            ...layout.rowBetween,
            padding: "18px 24px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div style={{ ...layout.row, gap: 10 }}>
            <Users size={18} color={colors.accent} />
            <h3
              style={{
                margin: 0,
                fontFamily: fonts.heading,
                fontSize: 17,
                color: colors.text,
              }}
            >
              {isEdit ? list.name : "New Audience List"}
            </h3>
          </div>
          <X
            size={18}
            color={colors.textSecondary}
            style={{ cursor: "pointer" }}
            onClick={onClose}
          />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* Instructions */}
          <div
            style={{
              padding: "10px 14px",
              marginBottom: 16,
              borderRadius: 6,
              background: `${colors.accent}08`,
              border: `1px solid ${colors.accent}20`,
              fontSize: 11,
              lineHeight: 1.5,
              color: colors.textSecondary,
            }}
          >
            Build a dynamic audience by setting filter rules below. Contacts
            matching <strong style={{ color: colors.text }}>all</strong>{" "}
            selected criteria are included automatically. Use{" "}
            <strong style={{ color: "#22c55e" }}>Include</strong> to add
            specific people who don't match the filters, or{" "}
            <strong style={{ color: "#ef4444" }}>Exclude</strong> to remove
            people who do. The preview updates live as you adjust.
          </div>

          {/* Name + Description */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <div>
              <label style={labelStyle}>Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Advisory Clients - Hot"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                style={inputStyle}
              />
            </div>
          </div>

          {/* Product */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Product</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {Object.entries(CONTACT_TYPES).map(([id, info]) => (
                <button
                  key={id}
                  onClick={() => toggleArrayFilter("contactTypes", id)}
                  style={chipStyle(
                    filters.contactTypes.includes(id),
                    info.color,
                  )}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Tags */}
          {ghlContentTags.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Content Tags</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {ghlContentTags.map((t) => (
                  <button
                    key={t.tag}
                    onClick={() => toggleArrayFilter("tags", t.tag)}
                    style={chipStyle(filters.tags.includes(t.tag), "#8b5cf6")}
                  >
                    {t.tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Engagement Tiers */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Engagement Tier</label>
            <div style={{ display: "flex", gap: 5 }}>
              {ENGAGEMENT_TIERS.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => toggleArrayFilter("engagementTiers", tier.id)}
                  style={chipStyle(
                    filters.engagementTiers.includes(tier.id),
                    tier.color,
                  )}
                >
                  {tier.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Tags (AND — contact must have all)</label>
            <div ref={tagDropdownRef} style={{ position: "relative" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    setTagDropdownOpen(true);
                  }}
                  onFocus={() => setTagDropdownOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                    if (e.key === "Escape") setTagDropdownOpen(false);
                  }}
                  placeholder={
                    allTags.length
                      ? "Search or type a tag..."
                      : "Type a tag and press Enter..."
                  }
                  style={{ ...inputStyle, flex: 1 }}
                />
                <Button variant="default" size="sm" onClick={addTag}>
                  Add
                </Button>
              </div>
              {tagDropdownOpen &&
                allTags.length > 0 &&
                (() => {
                  const filterTag = (t) =>
                    !tagInput ||
                    t.tag.toLowerCase().includes(tagInput.toLowerCase());
                  const filteredCrm = crmTags.filter(filterTag);
                  const filteredContact = ghlContactTags.filter(filterTag);
                  const filteredContent = ghlContentTags.filter(filterTag);
                  const totalFiltered =
                    filteredCrm.length +
                    filteredContact.length +
                    filteredContent.length;

                  const sectionHeader = (label) => (
                    <div
                      style={{
                        padding: "6px 10px 3px",
                        fontSize: 9,
                        fontWeight: 700,
                        color: colors.textSecondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontFamily: fonts.body,
                        borderTop: `1px solid ${colors.border}`,
                        marginTop: 2,
                      }}
                    >
                      {label}
                    </div>
                  );

                  const tagRow = (t) => {
                    const selected = filters.tags.includes(t.tag);
                    return (
                      <div
                        key={`${t.source || "crm"}-${t.tag}`}
                        onClick={() => {
                          if (!selected) {
                            updateFilter("tags", [...filters.tags, t.tag]);
                          }
                          setTagInput("");
                          setTagDropdownOpen(false);
                        }}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 10px",
                          fontSize: 12,
                          fontFamily: fonts.body,
                          cursor: selected ? "default" : "pointer",
                          color: selected ? colors.textSecondary : colors.text,
                          background: selected ? colors.bg : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!selected)
                            e.currentTarget.style.background = colors.bgHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = selected
                            ? colors.bg
                            : "transparent";
                        }}
                      >
                        <span>
                          {t.tag}
                          {selected ? " ✓" : ""}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            color: colors.textSecondary,
                            fontFamily: fonts.mono,
                          }}
                        >
                          {t.count}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 48,
                        zIndex: 10,
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 6,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        maxHeight: 280,
                        overflowY: "auto",
                        marginTop: -2,
                      }}
                    >
                      {filteredContact.length > 0 && (
                        <>
                          {sectionHeader("Contact Tags")}
                          {filteredContact.map(tagRow)}
                        </>
                      )}
                      {filteredContent.length > 0 && (
                        <>
                          {sectionHeader("Content Tags")}
                          {filteredContent.map(tagRow)}
                        </>
                      )}
                      {filteredCrm.length > 0 && (
                        <>
                          {sectionHeader("Insights Tags")}
                          {filteredCrm.map(tagRow)}
                        </>
                      )}
                      {totalFiltered === 0 && (
                        <div
                          style={{
                            padding: "8px 10px",
                            fontSize: 11,
                            color: colors.textSecondary,
                          }}
                        >
                          No matching tags — press Enter to add &quot;{tagInput}
                          &quot;
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
            {filters.tags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {filters.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      background: `${colors.accent}15`,
                      border: `1px solid ${colors.accent}40`,
                      borderRadius: 4,
                      fontSize: 11,
                      color: colors.accent,
                    }}
                  >
                    {tag}
                    <X
                      size={10}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        updateFilter(
                          "tags",
                          filters.tags.filter((t) => t !== tag),
                        )
                      }
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Companies */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Company</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addCompany())
                }
                placeholder="Type a company name and press Enter..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <Button variant="default" size="sm" onClick={addCompany}>
                Add
              </Button>
            </div>
            {filters.companies.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {filters.companies.map((c) => (
                  <span
                    key={c}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      background: "rgba(99,102,241,0.1)",
                      border: "1px solid rgba(99,102,241,0.3)",
                      borderRadius: 4,
                      fontSize: 11,
                      color: "#6366f1",
                    }}
                  >
                    {c}
                    <X
                      size={10}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        updateFilter(
                          "companies",
                          filters.companies.filter((co) => co !== c),
                        )
                      }
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Include Contacts */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              <UserPlus
                size={10}
                style={{ marginRight: 4, verticalAlign: "middle" }}
              />
              Include Contacts
            </label>
            <p
              style={{
                fontSize: 10,
                color: colors.textSecondary,
                margin: "0 0 6px",
                lineHeight: 1.4,
              }}
            >
              Add specific contacts who should always receive this, even if they
              don't match the filters above.
            </p>
            <div ref={includeRef} style={{ position: "relative" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input
                  value={includeSearch}
                  onChange={(e) => {
                    setIncludeSearch(e.target.value);
                    setIncludeDropdownOpen(true);
                  }}
                  onFocus={() =>
                    includeSearch.trim() && setIncludeDropdownOpen(true)
                  }
                  placeholder="Search by name or email to add..."
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
              {includeDropdownOpen && includeResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    maxHeight: 200,
                    overflowY: "auto",
                    marginTop: -2,
                  }}
                >
                  {includeResults
                    .filter(
                      (c) => !(filters.includeEmails || []).includes(c.email),
                    )
                    .map((c) => (
                      <div
                        key={c.email}
                        onClick={() => {
                          updateFilter("includeEmails", [
                            ...(filters.includeEmails || []),
                            c.email,
                          ]);
                          setIncludeSearch("");
                          setIncludeDropdownOpen(false);
                        }}
                        style={{
                          padding: "6px 10px",
                          fontSize: 12,
                          fontFamily: fonts.body,
                          cursor: "pointer",
                          color: colors.text,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = colors.bgHover)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <span style={{ fontWeight: 500 }}>
                          {c.firstName} {c.lastName}
                        </span>
                        <span
                          style={{
                            marginLeft: 6,
                            color: colors.textSecondary,
                            fontSize: 11,
                          }}
                        >
                          {c.email}
                        </span>
                        {c.company && (
                          <span
                            style={{
                              marginLeft: 6,
                              color: colors.textSecondary,
                              fontSize: 10,
                            }}
                          >
                            {c.company}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
            {(filters.includeEmails || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {filters.includeEmails.map((email) => (
                  <span
                    key={email}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      background: "rgba(34,197,94,0.1)",
                      border: "1px solid rgba(34,197,94,0.3)",
                      borderRadius: 4,
                      fontSize: 11,
                      color: "#22c55e",
                    }}
                  >
                    {email}
                    <X
                      size={10}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        updateFilter(
                          "includeEmails",
                          filters.includeEmails.filter((e) => e !== email),
                        )
                      }
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Exclude Contacts */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              <UserMinus
                size={10}
                style={{ marginRight: 4, verticalAlign: "middle" }}
              />
              Exclude Contacts
            </label>
            <p
              style={{
                fontSize: 10,
                color: colors.textSecondary,
                margin: "0 0 6px",
                lineHeight: 1.4,
              }}
            >
              Remove specific contacts who should never receive this, even if
              they match the filters above.
            </p>
            <div ref={excludeRef} style={{ position: "relative" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input
                  value={excludeSearch}
                  onChange={(e) => {
                    setExcludeSearch(e.target.value);
                    setExcludeDropdownOpen(true);
                  }}
                  onFocus={() =>
                    excludeSearch.trim() && setExcludeDropdownOpen(true)
                  }
                  placeholder="Search by name or email to exclude..."
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
              {excludeDropdownOpen && excludeResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    maxHeight: 200,
                    overflowY: "auto",
                    marginTop: -2,
                  }}
                >
                  {excludeResults
                    .filter(
                      (c) => !(filters.excludeEmails || []).includes(c.email),
                    )
                    .map((c) => (
                      <div
                        key={c.email}
                        onClick={() => {
                          updateFilter("excludeEmails", [
                            ...(filters.excludeEmails || []),
                            c.email,
                          ]);
                          setExcludeSearch("");
                          setExcludeDropdownOpen(false);
                        }}
                        style={{
                          padding: "6px 10px",
                          fontSize: 12,
                          fontFamily: fonts.body,
                          cursor: "pointer",
                          color: colors.text,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = colors.bgHover)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <span style={{ fontWeight: 500 }}>
                          {c.firstName} {c.lastName}
                        </span>
                        <span
                          style={{
                            marginLeft: 6,
                            color: colors.textSecondary,
                            fontSize: 11,
                          }}
                        >
                          {c.email}
                        </span>
                        {c.company && (
                          <span
                            style={{
                              marginLeft: 6,
                              color: colors.textSecondary,
                              fontSize: 10,
                            }}
                          >
                            {c.company}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
            {(filters.excludeEmails || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {filters.excludeEmails.map((email) => (
                  <span
                    key={email}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      borderRadius: 4,
                      fontSize: 11,
                      color: "#ef4444",
                    }}
                  >
                    {email}
                    <X
                      size={10}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        updateFilter(
                          "excludeEmails",
                          filters.excludeEmails.filter((e) => e !== email),
                        )
                      }
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Decision Maker + Email Frequency + Search */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Decision Maker</label>
              <select
                value={
                  filters.isDecisionMaker === null
                    ? ""
                    : String(filters.isDecisionMaker)
                }
                onChange={(e) =>
                  updateFilter(
                    "isDecisionMaker",
                    e.target.value === "" ? null : e.target.value === "true",
                  )
                }
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="">Any</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Email Frequency</label>
              <select
                value={filters.emailFrequency || ""}
                onChange={(e) =>
                  updateFilter("emailFrequency", e.target.value || null)
                }
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="">Any</option>
                <option value="all">All emails</option>
                <option value="digest">Digest only</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Bounce Status</label>
              <select
                value={filters.bounceStatus || ""}
                onChange={(e) =>
                  updateFilter("bounceStatus", e.target.value || null)
                }
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="">Any</option>
                <option value="clean">Clean</option>
                <option value="hard">Hard bounce</option>
              </select>
            </div>
          </div>

          {/* Country + Sector */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Country</label>
              <div style={{ display: "flex", gap: 4 }}>
                <input
                  value={countryInput}
                  onChange={(e) => setCountryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && countryInput.trim()) {
                      e.preventDefault();
                      const val = countryInput.trim();
                      if (!filters.countries.some((c) => c.toLowerCase() === val.toLowerCase())) {
                        updateFilter("countries", [...filters.countries, val]);
                      }
                      setCountryInput("");
                    }
                  }}
                  placeholder="Type and press Enter..."
                  style={inputStyle}
                />
              </div>
              {filters.countries.length > 0 && (
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 4 }}>
                  {filters.countries.map((c) => (
                    <span
                      key={c}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        fontSize: 10,
                        padding: "2px 6px",
                        borderRadius: 3,
                        background: "rgba(99,102,241,0.08)",
                        color: colors.info,
                        border: `1px solid rgba(99,102,241,0.2)`,
                      }}
                    >
                      {c}
                      <X
                        size={10}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          updateFilter("countries", filters.countries.filter((x) => x !== c))
                        }
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={labelStyle}>Sector</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {SECTORS.map((s) => {
                  const active = filters.sectors.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() =>
                        updateFilter(
                          "sectors",
                          active
                            ? filters.sectors.filter((x) => x !== s)
                            : [...filters.sectors, s],
                        )
                      }
                      style={{
                        padding: "3px 8px",
                        borderRadius: 4,
                        border: `1px solid ${active ? "#7C3AED" : colors.border}`,
                        background: active ? "rgba(124,58,237,0.08)" : "transparent",
                        color: active ? "#7C3AED" : colors.textSecondary,
                        fontSize: 10,
                        fontFamily: fonts.body,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Search text */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Search (name, email, company)</label>
            <div style={{ position: "relative" }}>
              <Search
                size={14}
                color={colors.textSecondary}
                style={{ position: "absolute", left: 10, top: 9 }}
              />
              <input
                value={filters.searchText}
                onChange={(e) => updateFilter("searchText", e.target.value)}
                placeholder="Free text search..."
                style={{ ...inputStyle, paddingLeft: 30 }}
              />
            </div>
          </div>

          {/* Exclude suppressed toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 20,
            }}
          >
            <input
              type="checkbox"
              checked={excludeSuppressed}
              onChange={(e) => setExcludeSuppressed(e.target.checked)}
              id="excl-supp"
            />
            <label
              htmlFor="excl-supp"
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                cursor: "pointer",
              }}
            >
              Exclude unsubscribed, bounced, and suppressed contacts
            </label>
          </div>

          {/* Share with */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Share With</label>
            {sharedWith.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 5,
                  marginBottom: 8,
                }}
              >
                {sharedWith.map((email) => (
                  <span
                    key={email}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      borderRadius: 5,
                      fontSize: 11,
                      background: `${colors.accent}15`,
                      border: `1px solid ${colors.accent}30`,
                      color: colors.text,
                    }}
                  >
                    {email}
                    <button
                      onClick={() =>
                        setSharedWith((prev) =>
                          prev.filter((e) => e !== email),
                        )
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        lineHeight: 1,
                        fontSize: 13,
                        color: colors.textSecondary,
                      }}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              {appUsers.filter((u) => !sharedWith.includes(u)).length > 0 ? (
                <select
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && !sharedWith.includes(val)) {
                      setSharedWith((prev) => [...prev, val]);
                    }
                  }}
                  style={{ ...inputStyle, flex: 1, color: colors.textSecondary }}
                >
                  <option value="">Select a colleague...</option>
                  {appUsers
                    .filter((u) => !sharedWith.includes(u))
                    .map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                </select>
              ) : null}
              <input
                placeholder="Type an email address..."
                style={{ ...inputStyle, flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const val = e.target.value.trim().toLowerCase();
                    if (val && val.includes("@") && !sharedWith.includes(val)) {
                      setSharedWith((prev) => [...prev, val]);
                      e.target.value = "";
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Preview panel */}
          <div
            style={{
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: colors.text,
                  fontFamily: fonts.body,
                }}
              >
                Preview
              </span>
              {previewing ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 11,
                    color: colors.textSecondary,
                  }}
                >
                  <Loader2
                    size={12}
                    className="spin"
                  />{" "}
                  Matching...
                </span>
              ) : preview ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: preview.total > 0 ? colors.accent : colors.textSecondary,
                    }}
                  >
                    {preview.total.toLocaleString()} contact
                    {preview.total !== 1 ? "s" : ""}
                  </span>
                  {preview.contacts?.length > 0 && (
                    <button
                      onClick={() => setGroupByCompany((v) => !v)}
                      title={groupByCompany ? "Show flat list" : "Group by company"}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        padding: "2px 8px",
                        borderRadius: 4,
                        border: `1px solid ${groupByCompany ? colors.accent : colors.border}`,
                        background: groupByCompany ? colors.accentDim : "transparent",
                        color: groupByCompany ? colors.accent : colors.textSecondary,
                        fontSize: 10,
                        fontFamily: fonts.body,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <Building2 size={10} />
                      Company
                    </button>
                  )}
                </div>
              ) : null}
            </div>
            {preview && preview.contacts && preview.contacts.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: groupByCompany ? 8 : 4 }}>
                {groupByCompany ? (
                  (() => {
                    const groups = {};
                    preview.contacts.forEach((c) => {
                      const key = c.company || "No Company";
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(c);
                    });
                    const sorted = Object.entries(groups).sort((a, b) =>
                      a[0] === "No Company" ? 1 : b[0] === "No Company" ? -1 : a[0].localeCompare(b[0]),
                    );
                    return sorted.map(([company, contacts]) => (
                      <div key={company}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 10,
                            fontWeight: 700,
                            color: colors.textSecondary,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            padding: "2px 8px",
                            marginBottom: 3,
                          }}
                        >
                          <Building2 size={10} />
                          {company}
                          <span style={{ fontWeight: 500, color: colors.textSecondary }}>
                            ({contacts.length})
                          </span>
                        </div>
                        {contacts.map((c) => (
                          <div
                            key={c.email}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "4px 8px 4px 24px",
                              borderRadius: 4,
                              background: colors.bgCard,
                              fontSize: 11,
                              marginBottom: 2,
                            }}
                          >
                            <span style={{ color: colors.text }}>
                              {c.firstName} {c.lastName}
                            </span>
                            <span style={{ color: colors.textSecondary }}>{c.email}</span>
                          </div>
                        ))}
                      </div>
                    ));
                  })()
                ) : (
                  preview.contacts.map((c) => (
                    <div
                      key={c.email}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 8px",
                        borderRadius: 4,
                        background: colors.bgCard,
                        fontSize: 11,
                      }}
                    >
                      <span style={{ color: colors.text }}>
                        {c.firstName} {c.lastName}
                      </span>
                      <span style={{ color: colors.textSecondary }}>{c.email}</span>
                      {c.company && (
                        <span
                          style={{ color: colors.textSecondary, fontSize: 10 }}
                        >
                          {c.company}
                        </span>
                      )}
                    </div>
                  ))
                )}
                {preview.total > 10 && (
                  <span
                    style={{
                      fontSize: 10,
                      color: colors.textSecondary,
                      textAlign: "center",
                      paddingTop: 4,
                    }}
                  >
                    + {(preview.total - 10).toLocaleString()} more
                  </span>
                )}
              </div>
            ) : !previewing ? (
              <span style={{ fontSize: 11, color: colors.textSecondary }}>
                Add filters to see matching contacts
              </span>
            ) : null}
          </div>

          {error && (
            <div style={{ ...surfaces.errorBanner, marginTop: 12 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            ...layout.row,
            justifyContent: "flex-end",
            padding: "14px 24px",
            borderTop: `1px solid ${colors.border}`,
            gap: 8,
          }}
        >
          <Button variant="default" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={Save}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : isEdit ? "Update List" : "Create List"}
          </Button>
        </div>
      </div>
    </>
  );
}
